import { API_BASE } from "./common";
import { attemptSilentRefresh } from "./auth-utils";

/**
 * A wrapper around fetch that handles Cloudflare Access session recovery.
 * It adds the X-Requested-With header to force 401s instead of 302s,
 * and attempts a silent refresh on 401.
 */
export const fetchWithInterceptor = async (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> => {
  // Add X-Requested-With header to force 401s
  const headers = new Headers(init?.headers);
  headers.set("X-Requested-With", "XMLHttpRequest");

  const config: RequestInit = {
    ...init,
    headers,
  };

  // Clone the request if input is a Request object, to allow retry?
  // Since most usage in this app is fetch(url, init) where init.body is string/FormData,
  // simple retry works. If input is a Request object with a stream body, retry might fail.
  // We assume standard usage here.

  const response = await fetch(input, config);

  if (response.status === 401) {
    const refreshed = await attemptSilentRefresh();
    if (refreshed) {
      // Retry the original request
      return fetch(input, config);
    }

    // Redirect to login if refresh fails
    const returnUrl = encodeURIComponent(window.location.href);
    let loginBase: string;
    try {
      // If API_BASE is absolute, use its origin
      loginBase = new URL(API_BASE).origin;
    } catch {
      // If API_BASE is relative, use current origin (empty string implies relative path)
      loginBase = "";
    }

    const loginPath = "/cdn-cgi/access/login";
    const fullLoginUrl = `${loginBase}${loginPath}?return_url=${returnUrl}`;

    window.location.href = fullLoginUrl;

    return response;
  }

  return response;
};
